import {
    CalendarDays,
    Clock,
    ExternalLink,
    MapPin,
    Users,
    Video,
    X,
} from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
    cancelClubEventAttendance,
    fetchClubEventById,
    isActiveClubEventRegistration,
    isClubEventConfirmedRegistration,
    isClubEventWaitlisted,
    registerForClubEvent,
} from '../api/clubEvents';
import {
    cancelMeetingRegistration,
    fetchMeetingById,
    isActiveMeetingRegistration,
    registerForMeeting,
} from '../api/meetings';
import { ClubEventGuestManagePanel } from './ClubEventGuestManagePanel';
import { MeetingGuestJoinModal } from './MeetingGuestJoinModal';
import { MeetingGuestManagePanel } from './MeetingGuestManagePanel';
import { MeetingParticipantsPanel } from './MeetingParticipantsPanel';

/**
 * @typedef {import('../utils/calendarEventSelection').CalendarEventSelection} CalendarEventSelection
 */

/**
 * @param {string | undefined} iso
 * @param {string} locale
 * @param {{ dateStyle?: 'full' | 'long' | 'medium', timeStyle?: 'short' }} [opts]
 */
function formatInstant(iso, locale, opts = {}) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat(locale, opts).format(d);
}

/**
 * @param {{
 *   open: boolean,
 *   selection: CalendarEventSelection | null,
 *   authToken: string | null,
 *   useLiveApi: boolean,
 *   onClose: () => void,
 * }} props
 */
export function EventCalendarDetailModal({
    open,
    selection,
    authToken,
    useLiveApi,
    onClose,
}) {
    const { t, i18n } = useTranslation();
    const titleId = useId();
    const locale = i18n.language?.toLowerCase().startsWith('en')
        ? 'en-GB'
        : 'tr-TR';

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(/** @type {string | null} */ (null));
    const [clubDetail, setClubDetail] = useState(
        /** @type {import('../api/clubEvents').ClubEventDetail | null} */ (null),
    );
    const [meetingDetail, setMeetingDetail] = useState(
        /** @type {import('../api/meetings').MeetingDetail | null} */ (null),
    );
    const [guestModalOpen, setGuestModalOpen] = useState(false);
    const [registerSubmitting, setRegisterSubmitting] = useState(false);
    const [cancelSubmitting, setCancelSubmitting] = useState(false);
    const [registerError, setRegisterError] = useState(
        /** @type {string | null} */ (null),
    );
    const [cancelError, setCancelError] = useState(
        /** @type {string | null} */ (null),
    );
    const [eventRegisterError, setEventRegisterError] = useState(
        /** @type {string | null} */ (null),
    );
    const [guestManageBusy, setGuestManageBusy] = useState(false);
    const [participantsRefreshKey, setParticipantsRefreshKey] = useState(0);

    const isMeeting = selection?.kind === 'weekly-meeting';
    const isClubEvent =
        selection?.kind === 'event' && selection?.source === 'club-event';

    const detailFetchTarget = useMemo(() => {
        if (!selection?.sourceId) return null;
        if (
            selection.kind === 'event' &&
            selection.source === 'club-event'
        ) {
            return 'club-event';
        }
        if (
            selection.kind === 'weekly-meeting' &&
            selection.source === 'meeting'
        ) {
            return 'meeting';
        }
        return null;
    }, [selection]);

    useEffect(() => {
        if (!open || !selection) {
            setLoading(false);
            setError(null);
            setClubDetail(null);
            setMeetingDetail(null);
            return undefined;
        }

        if (!detailFetchTarget || !useLiveApi || !authToken) {
            setLoading(false);
            setError(null);
            setClubDetail(null);
            setMeetingDetail(null);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);
        setClubDetail(null);
        setMeetingDetail(null);

        (async () => {
            try {
                if (detailFetchTarget === 'club-event') {
                    const detail = await fetchClubEventById(
                        authToken,
                        selection.sourceId,
                    );
                    if (cancelled) return;
                    setClubDetail(detail);
                } else {
                    const detail = await fetchMeetingById(
                        authToken,
                        selection.sourceId,
                    );
                    if (cancelled) return;
                    setMeetingDetail(detail);
                }
            } catch (err) {
                if (cancelled) return;
                setError(
                    err instanceof Error
                        ? err.message
                        : t('eventCalendarPage.detailLoadErrorGeneric'),
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, selection, detailFetchTarget, useLiveApi, authToken, t]);

    useEffect(() => {
        if (!open) return undefined;

        function onKeyDown(e) {
            if (e.key === 'Escape' && !loading && !guestModalOpen) {
                onClose();
            }
        }

        document.addEventListener('keydown', onKeyDown);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose, loading, guestModalOpen]);

    useEffect(() => {
        if (!open) {
            setGuestModalOpen(false);
            setRegisterSubmitting(false);
            setCancelSubmitting(false);
            setRegisterError(null);
            setCancelError(null);
            setEventRegisterError(null);
            setGuestManageBusy(false);
            setParticipantsRefreshKey(0);
        }
    }, [open, selection?.fcEventId]);

    const allowMeetingGuests = meetingDetail?.allowGuests === true;
    const allowClubEventGuests = clubDetail?.allowGuests === true;
    const canRegisterMeeting =
        useLiveApi &&
        Boolean(authToken) &&
        Boolean(selection?.sourceId) &&
        selection?.source === 'meeting';

    const canRegisterClubEvent =
        useLiveApi &&
        Boolean(authToken) &&
        Boolean(selection?.sourceId) &&
        selection?.source === 'club-event';

    async function reloadClubEventDetail() {
        if (!authToken || !selection?.sourceId) return null;
        const detail = await fetchClubEventById(authToken, selection.sourceId);
        setClubDetail(detail);
        return detail;
    }

    async function reloadMeetingDetail() {
        if (!authToken || !selection?.sourceId) return;
        const detail = await fetchMeetingById(authToken, selection.sourceId);
        setMeetingDetail(detail);
        setParticipantsRefreshKey((key) => key + 1);
    }

    function handleJoinMeetingClick() {
        if (!canRegisterMeeting) {
            toast.info(t('eventCalendarPage.registerRequiresLiveApi'));
            return;
        }
        setRegisterError(null);
        setGuestModalOpen(true);
    }

    async function handleMeetingRegister(payload) {
        if (!authToken || !selection?.sourceId) {
            throw new Error(t('eventCalendarPage.registerRequiresLiveApi'));
        }

        setRegisterSubmitting(true);
        setRegisterError(null);

        try {
            await registerForMeeting(authToken, {
                meetingId: selection.sourceId,
                includeSubMember: payload.spouseAttending,
                guests: payload.guests,
            });
            await reloadMeetingDetail();
            toast.success(t('eventCalendarPage.registerSuccess'));
            setGuestModalOpen(false);
        } catch (err) {
            const msg =
                err instanceof Error
                    ? err.message
                    : t('eventCalendarPage.registerErrorGeneric');
            setRegisterError(msg);
            throw err;
        } finally {
            setRegisterSubmitting(false);
        }
    }

    async function handleJoinEventClick() {
        if (!canRegisterClubEvent || !authToken || !selection?.sourceId) {
            toast.info(t('eventCalendarPage.registerRequiresLiveApi'));
            return;
        }

        setRegisterSubmitting(true);
        setEventRegisterError(null);

        try {
            await registerForClubEvent(authToken, selection.sourceId);
            const detail = await reloadClubEventDetail();
            if (detail && isClubEventWaitlisted(detail)) {
                toast.info(
                    t('eventCalendarPage.eventWaitlistSuccess', {
                        queueNumber: detail.queueNumber ?? '—',
                    }),
                );
            } else {
                toast.success(t('eventCalendarPage.eventRegisterSuccess'));
            }
        } catch (err) {
            const msg =
                err instanceof Error
                    ? err.message
                    : t('eventCalendarPage.eventRegisterErrorGeneric');
            setEventRegisterError(msg);
            toast.error(msg);
        } finally {
            setRegisterSubmitting(false);
        }
    }

    async function handleCancelEventRegistration() {
        if (!canRegisterClubEvent || !authToken || !selection?.sourceId) {
            toast.info(t('eventCalendarPage.registerRequiresLiveApi'));
            return;
        }

        setCancelSubmitting(true);
        setCancelError(null);

        try {
            await cancelClubEventAttendance(authToken, selection.sourceId);
            await reloadClubEventDetail();
            toast.success(t('eventCalendarPage.cancelEventRegistrationSuccess'));
        } catch (err) {
            const msg =
                err instanceof Error
                    ? err.message
                    : t('eventCalendarPage.cancelEventRegistrationErrorGeneric');
            setCancelError(msg);
            toast.error(msg);
        } finally {
            setCancelSubmitting(false);
        }
    }

    async function handleCancelRegistration() {
        if (!authToken || !selection?.sourceId) {
            toast.info(t('eventCalendarPage.registerRequiresLiveApi'));
            return;
        }

        setCancelSubmitting(true);
        setCancelError(null);

        try {
            await cancelMeetingRegistration(authToken, selection.sourceId);
            await reloadMeetingDetail();
            toast.success(t('eventCalendarPage.cancelRegistrationSuccess'));
        } catch (err) {
            const msg =
                err instanceof Error
                    ? err.message
                    : t('eventCalendarPage.cancelRegistrationErrorGeneric');
            setCancelError(msg);
            toast.error(msg);
        } finally {
            setCancelSubmitting(false);
        }
    }

    const display = useMemo(() => {
        if (!selection) return null;

        const title =
            clubDetail?.title ||
            meetingDetail?.title ||
            selection.title;

        const description =
            clubDetail?.description ||
            meetingDetail?.description ||
            '';

        const startIso =
            clubDetail?.startDate ||
            meetingDetail?.meetingDate ||
            selection.startIso;
        const endIso =
            clubDetail?.endDate ||
            meetingDetail?.meetingEndDate ||
            selection.endIso;
        const allDay = selection.allDay && !clubDetail && !meetingDetail;

        const dateLabel = formatInstant(startIso, locale, { dateStyle: 'full' });

        let timeLabel = t('eventCalendarPage.agendaAllDay');
        if (!allDay && startIso) {
            const startTime = formatInstant(startIso, locale, {
                timeStyle: 'short',
            });
            const endTime = endIso
                ? formatInstant(endIso, locale, { timeStyle: 'short' })
                : '';
            timeLabel =
                endTime && endTime !== startTime
                    ? `${startTime} – ${endTime}`
                    : startTime;
        }

        const clubLocation = clubDetail?.location;

        let locationPrimary = '';
        let locationSecondary = '';

        if (meetingDetail) {
            if (meetingDetail.meetingRoomName) {
                locationPrimary = meetingDetail.meetingRoomName;
                if (
                    meetingDetail.location &&
                    meetingDetail.location !== meetingDetail.meetingRoomName
                ) {
                    locationSecondary = meetingDetail.location;
                }
            } else if (meetingDetail.location) {
                locationPrimary = meetingDetail.location;
            }
        } else if (clubLocation) {
            locationPrimary =
                clubLocation.venueName?.trim() ||
                clubLocation.city?.trim() ||
                '';
            locationSecondary = clubLocation.address?.trim() || '';
        } else {
            locationPrimary =
                selection.preview?.meetingRoomName?.trim() ||
                selection.preview?.location?.trim() ||
                selection.preview?.city?.trim() ||
                '';
        }

        const mapsQuery = locationSecondary || locationPrimary;
        const mapsUrl = mapsQuery
            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`
            : null;

        const capacity = clubDetail?.capacity ?? null;
        const registered =
            clubDetail?.registeredCount ??
            clubDetail?.participantCount ??
            null;

        const onlineMeetingLink =
            clubDetail?.onlineMeetingLink ??
            meetingDetail?.onlineMeetingUrl ??
            null;

        const mealPrice = meetingDetail?.mealPrice ?? null;
        const currency = meetingDetail?.currency ?? 'TRY';
        const price = clubDetail?.price ?? null;

        return {
            title,
            description,
            dateLabel,
            timeLabel,
            locationPrimary,
            locationSecondary,
            mapsUrl,
            capacity,
            registered,
            price,
            mealPrice,
            currency,
            onlineMeetingLink,
            isRegisteredForEvent: isActiveClubEventRegistration(clubDetail),
            isEventWaitlisted: isClubEventWaitlisted(clubDetail),
            isEventConfirmedRegistration:
                isClubEventConfirmedRegistration(clubDetail),
            eventQueueNumber: clubDetail?.queueNumber ?? null,
            isCompleted:
                clubDetail?.isCompleted === true ||
                meetingDetail?.isCompleted === true,
            attendanceStatusName:
                meetingDetail?.myAttendanceStatusName ?? null,
            isRegisteredForMeeting: isActiveMeetingRegistration(meetingDetail),
            myMeetingGuests: meetingDetail?.myGuests ?? [],
            myClubEventGuests: clubDetail?.myGuests ?? [],
        };
    }, [selection, clubDetail, meetingDetail, locale, t]);

    if (!open || !selection || !display) return null;

    const kindLabel =
        selection.kind === 'weekly-meeting'
            ? t('eventCalendarPage.legendMeeting')
            : t('eventCalendarPage.legendEvent');

    const priceLabel =
        display.mealPrice != null
            ? display.mealPrice > 0
                ? t('eventCalendarPage.detailMealPrice', {
                      price: display.mealPrice,
                      currency: display.currency,
                  })
                : t('eventCalendarPage.detailFree')
            : display.price != null
              ? display.price > 0
                  ? t('eventCalendarPage.detailPrice', {
                        price: display.price,
                    })
                  : t('eventCalendarPage.detailFree')
              : null;

    const showJoinMeeting =
        isMeeting &&
        !loading &&
        !display.isCompleted &&
        !display.isRegisteredForMeeting &&
        canRegisterMeeting;

    const showJoinEvent =
        isClubEvent &&
        !loading &&
        !display.isCompleted &&
        !display.isRegisteredForEvent &&
        canRegisterClubEvent;

    const showCancelRegistration =
        isMeeting &&
        !loading &&
        !display.isCompleted &&
        display.isRegisteredForMeeting &&
        canRegisterMeeting;

    const showCancelEventRegistration =
        isClubEvent &&
        !loading &&
        !display.isCompleted &&
        display.isRegisteredForEvent &&
        canRegisterClubEvent;

    const showGuestManagement =
        isMeeting &&
        !loading &&
        !display.isCompleted &&
        display.isRegisteredForMeeting &&
        allowMeetingGuests &&
        canRegisterMeeting;

    const showEventGuestManagement =
        isClubEvent &&
        !loading &&
        !display.isCompleted &&
        display.isRegisteredForEvent &&
        allowClubEventGuests &&
        canRegisterClubEvent;

    const showMeetingParticipants =
        isMeeting && !loading && canRegisterMeeting && Boolean(authToken);

    const meetingActionBusy =
        registerSubmitting || cancelSubmitting || guestManageBusy;

    const eventActionBusy =
        registerSubmitting || cancelSubmitting || guestManageBusy;

    return (
        <div
            className="app-modal-backdrop"
            onClick={loading || guestModalOpen ? undefined : onClose}
            role="presentation"
        >
            <div
                className="app-modal app-modal--wide event-calendar-detail-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className={`event-calendar-detail-modal-accent event-calendar-detail-modal-accent--${selection.kind}`}
                    aria-hidden
                />
                <div className="app-modal-header event-calendar-detail-modal-header">
                    <div className="min-w-0">
                        <span
                            className={`event-calendar-detail-kind-badge event-calendar-detail-kind-badge--${selection.kind}`}
                        >
                            {kindLabel}
                        </span>
                        <h2
                            id={titleId}
                            className="app-modal-title event-calendar-detail-modal-title h5 mb-0 mt-2"
                        >
                            {display.title}
                        </h2>
                    </div>
                    <button
                        type="button"
                        className="app-modal-close btn btn-link p-1"
                        onClick={onClose}
                        disabled={loading}
                        aria-label={t('eventCalendarPage.detailClose')}
                    >
                        <X size={20} strokeWidth={2} aria-hidden />
                    </button>
                </div>

                <div className="app-modal-body app-modal-body--scroll event-calendar-detail-modal-body">
                    {loading ? (
                        <p
                            className="event-calendar-detail-loading mb-0"
                            role="status"
                            aria-live="polite"
                        >
                            {t('eventCalendarPage.detailLoading')}
                        </p>
                    ) : (
                        <>
                            <ul className="event-calendar-detail-facts list-unstyled mb-0">
                                <li className="event-calendar-detail-fact">
                                    <CalendarDays
                                        size={18}
                                        strokeWidth={1.85}
                                        className="event-calendar-detail-fact-icon"
                                        aria-hidden
                                    />
                                    <div>
                                        <span className="event-calendar-detail-fact-label">
                                            {t('eventCalendarPage.detailDate')}
                                        </span>
                                        <span className="event-calendar-detail-fact-value">
                                            {display.dateLabel}
                                        </span>
                                    </div>
                                </li>
                                <li className="event-calendar-detail-fact">
                                    <Clock
                                        size={18}
                                        strokeWidth={1.85}
                                        className="event-calendar-detail-fact-icon"
                                        aria-hidden
                                    />
                                    <div>
                                        <span className="event-calendar-detail-fact-label">
                                            {t('eventCalendarPage.detailTime')}
                                        </span>
                                        <span className="event-calendar-detail-fact-value">
                                            {display.timeLabel}
                                        </span>
                                    </div>
                                </li>
                                {display.locationPrimary ||
                                display.locationSecondary ? (
                                    <li className="event-calendar-detail-fact">
                                        <MapPin
                                            size={18}
                                            strokeWidth={1.85}
                                            className="event-calendar-detail-fact-icon"
                                            aria-hidden
                                        />
                                        <div>
                                            <span className="event-calendar-detail-fact-label">
                                                {t(
                                                    'eventCalendarPage.detailLocation',
                                                )}
                                            </span>
                                            {display.locationPrimary ? (
                                                <span className="event-calendar-detail-fact-value d-block">
                                                    {display.locationPrimary}
                                                </span>
                                            ) : null}
                                            {display.locationSecondary ? (
                                                <span className="event-calendar-detail-fact-subvalue d-block">
                                                    {display.locationSecondary}
                                                </span>
                                            ) : null}
                                            {display.mapsUrl ? (
                                                <a
                                                    href={display.mapsUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="event-calendar-detail-link d-inline-flex align-items-center gap-1 mt-1"
                                                >
                                                    {t(
                                                        'eventCalendarPage.detailOpenMaps',
                                                    )}
                                                    <ExternalLink
                                                        size={14}
                                                        strokeWidth={2}
                                                        aria-hidden
                                                    />
                                                </a>
                                            ) : null}
                                        </div>
                                    </li>
                                ) : null}
                                {display.capacity != null ? (
                                    <li className="event-calendar-detail-fact">
                                        <Users
                                            size={18}
                                            strokeWidth={1.85}
                                            className="event-calendar-detail-fact-icon"
                                            aria-hidden
                                        />
                                        <div>
                                            <span className="event-calendar-detail-fact-label">
                                                {t(
                                                    'eventCalendarPage.detailCapacity',
                                                )}
                                            </span>
                                            <span className="event-calendar-detail-fact-value">
                                                {t(
                                                    'eventCalendarPage.detailCapacityValue',
                                                    {
                                                        registered:
                                                            display.registered ??
                                                            0,
                                                        capacity:
                                                            display.capacity,
                                                    },
                                                )}
                                            </span>
                                        </div>
                                    </li>
                                ) : null}
                                {display.attendanceStatusName ? (
                                    <li className="event-calendar-detail-fact">
                                        <Users
                                            size={18}
                                            strokeWidth={1.85}
                                            className="event-calendar-detail-fact-icon"
                                            aria-hidden
                                        />
                                        <div>
                                            <span className="event-calendar-detail-fact-label">
                                                {t(
                                                    'eventCalendarPage.detailAttendanceStatus',
                                                )}
                                            </span>
                                            <span className="event-calendar-detail-fact-value">
                                                {display.attendanceStatusName}
                                            </span>
                                        </div>
                                    </li>
                                ) : null}
                                {display.onlineMeetingLink ? (
                                    <li className="event-calendar-detail-fact">
                                        <Video
                                            size={18}
                                            strokeWidth={1.85}
                                            className="event-calendar-detail-fact-icon"
                                            aria-hidden
                                        />
                                        <div>
                                            <span className="event-calendar-detail-fact-label">
                                                {t(
                                                    'eventCalendarPage.detailOnlineMeeting',
                                                )}
                                            </span>
                                            <a
                                                href={display.onlineMeetingLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="event-calendar-detail-link d-inline-flex align-items-center gap-1"
                                            >
                                                {t(
                                                    'eventCalendarPage.detailJoinOnline',
                                                )}
                                                <ExternalLink
                                                    size={14}
                                                    strokeWidth={2}
                                                    aria-hidden
                                                />
                                            </a>
                                        </div>
                                    </li>
                                ) : null}
                            </ul>

                            {display.description ? (
                                <section
                                    className="event-calendar-detail-description mt-4"
                                    aria-label={t(
                                        'eventCalendarPage.detailDescription',
                                    )}
                                >
                                    <h3 className="event-calendar-detail-section-title h6">
                                        {t(
                                            'eventCalendarPage.detailDescription',
                                        )}
                                    </h3>
                                    <p className="event-calendar-detail-description-text mb-0">
                                        {display.description}
                                    </p>
                                </section>
                            ) : null}

                            {display.isEventWaitlisted ? (
                                <p
                                    className="event-calendar-detail-status event-calendar-detail-status--waitlist mb-0 mt-3"
                                    role="status"
                                >
                                    {display.eventQueueNumber != null
                                        ? t(
                                              'eventCalendarPage.detailEventWaitlisted',
                                              {
                                                  queueNumber:
                                                      display.eventQueueNumber,
                                              },
                                          )
                                        : t(
                                              'eventCalendarPage.detailEventWaitlistedGeneric',
                                          )}
                                </p>
                            ) : display.isEventConfirmedRegistration ? (
                                <p
                                    className="event-calendar-detail-status event-calendar-detail-status--registered mb-0 mt-3"
                                    role="status"
                                >
                                    {t('eventCalendarPage.detailRegistered')}
                                </p>
                            ) : null}

                            {display.isCompleted ? (
                                <p
                                    className="event-calendar-detail-status event-calendar-detail-status--completed mb-0 mt-3"
                                    role="status"
                                >
                                    {selection.kind === 'weekly-meeting'
                                        ? t(
                                              'eventCalendarPage.detailMeetingCompleted',
                                          )
                                        : t(
                                              'eventCalendarPage.detailCompleted',
                                          )}
                                </p>
                            ) : null}

                            {priceLabel ? (
                                <p className="event-calendar-detail-price mb-0 mt-3">
                                    {priceLabel}
                                </p>
                            ) : null}

                            {showMeetingParticipants && authToken ? (
                                <MeetingParticipantsPanel
                                    meetingId={selection.sourceId}
                                    authToken={authToken}
                                    refreshKey={participantsRefreshKey}
                                />
                            ) : null}

                            {showJoinEvent ? (
                                <div className="event-calendar-detail-actions mt-4 pt-3">
                                    <button
                                        type="button"
                                        className="btn btn-primary w-100 event-calendar-detail-join-btn"
                                        onClick={handleJoinEventClick}
                                        disabled={eventActionBusy}
                                    >
                                        {registerSubmitting
                                            ? t(
                                                  'eventCalendarPage.registerSubmitting',
                                              )
                                            : t(
                                                  'eventCalendarPage.detailJoinEvent',
                                              )}
                                    </button>
                                    {eventRegisterError ? (
                                        <p
                                            className="app-modal-error small text-danger mb-0 mt-2"
                                            role="alert"
                                        >
                                            {eventRegisterError}
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}

                            {showCancelEventRegistration ? (
                                <div className="event-calendar-detail-actions mt-4 pt-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger w-100 event-calendar-detail-cancel-btn"
                                        onClick={handleCancelEventRegistration}
                                        disabled={eventActionBusy}
                                    >
                                        {cancelSubmitting
                                            ? t(
                                                  'eventCalendarPage.cancelEventRegistrationSubmitting',
                                              )
                                            : t(
                                                  'eventCalendarPage.cancelEventRegistration',
                                              )}
                                    </button>
                                    {cancelError ? (
                                        <p
                                            className="app-modal-error small text-danger mb-0 mt-2"
                                            role="alert"
                                        >
                                            {cancelError}
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}

                            {showJoinMeeting ? (
                                <div className="event-calendar-detail-actions mt-4 pt-3">
                                    <button
                                        type="button"
                                        className="btn btn-primary w-100 event-calendar-detail-join-btn"
                                        onClick={handleJoinMeetingClick}
                                        disabled={meetingActionBusy}
                                    >
                                        {t(
                                            'eventCalendarPage.detailJoinMeeting',
                                        )}
                                    </button>
                                </div>
                            ) : null}

                            {showEventGuestManagement && authToken ? (
                                <ClubEventGuestManagePanel
                                    eventId={selection.sourceId}
                                    eventTitle={display.title}
                                    authToken={authToken}
                                    guests={display.myClubEventGuests}
                                    disabled={
                                        registerSubmitting || cancelSubmitting
                                    }
                                    onUpdated={reloadClubEventDetail}
                                    onBusyChange={setGuestManageBusy}
                                />
                            ) : null}

                            {showGuestManagement ? (
                                <MeetingGuestManagePanel
                                    meetingId={selection.sourceId}
                                    meetingTitle={display.title}
                                    authToken={authToken}
                                    guests={display.myMeetingGuests}
                                    disabled={
                                        registerSubmitting || cancelSubmitting
                                    }
                                    onUpdated={reloadMeetingDetail}
                                    onBusyChange={setGuestManageBusy}
                                />
                            ) : null}

                            {showCancelRegistration ? (
                                <div className="event-calendar-detail-actions mt-4 pt-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger w-100 event-calendar-detail-cancel-btn"
                                        onClick={handleCancelRegistration}
                                        disabled={meetingActionBusy}
                                    >
                                        {cancelSubmitting
                                            ? t(
                                                  'eventCalendarPage.cancelRegistrationSubmitting',
                                              )
                                            : t(
                                                  'eventCalendarPage.cancelRegistration',
                                              )}
                                    </button>
                                    {cancelError ? (
                                        <p
                                            className="app-modal-error small text-danger mb-0 mt-2"
                                            role="alert"
                                        >
                                            {cancelError}
                                        </p>
                                    ) : null}
                                </div>
                            ) : null}
                        </>
                    )}

                    {error ? (
                        <p
                            className="app-modal-error small text-danger mb-0 mt-3"
                            role="alert"
                        >
                            {error}
                        </p>
                    ) : null}
                </div>
            </div>

            <MeetingGuestJoinModal
                open={guestModalOpen}
                allowGuests={allowMeetingGuests}
                meetingTitle={display.title}
                submitting={registerSubmitting}
                submitError={registerError}
                onClose={() => {
                    if (registerSubmitting) return;
                    setGuestModalOpen(false);
                    setRegisterError(null);
                }}
                onSubmit={handleMeetingRegister}
            />
        </div>
    );
}
